import React, { useState, useEffect } from 'react';
import { Row, Col, Card } from 'react-bootstrap';
import {
  FaPencilAlt,
  FaTimes,
  FaPhoneAlt,
  FaEnvelope,
  FaLandmark,
  FaMapMarkerAlt,
} from 'react-icons/fa';
import { MdOutlineFileUpload } from 'react-icons/md';
import { useDispatch, useSelector } from 'react-redux';

import Wrapper from '../../components/Wrapper';
import UpdateProfile from '../../components/Forms/UpdateProfile';
import ProfilePictureModal from './ProfilePictureModal';
import { fetchProfile } from '../../store/features/profileSlice';
import { getRole } from '../../utils/role';
import '../../styles/profile.css';

function Profile() {
  const dispatch = useDispatch();
  const role = getRole();
  
  const [editInfo, setEditInfo] = useState(false);
  const [modalShow, setModalShow] = useState(false);
  
  const { status, profile } = useSelector((state) => state.profile);

  useEffect(() => {
    dispatch(fetchProfile());
  }, [dispatch]);

  // Helper for avatar display
//   const getAvatarSrc = () => {
//     if (profile.profile_picture && profile.profile_picture !== 'Default.jpg') {
//       return profile.profile_picture;
//     }
//     // Handle Farmer avatar logic if different (e.g., first letter)
//     return userAvatar;
//   };

  const canEditImage = role === 'FIELD_OFFICER';

  return (
    <Wrapper>
      {status === 'loading' ? (
        <h4 className="text-center mt-5">Loading Profile...</h4>
      ) : (
        <div className='profile-page py-4'>
          <Row>
            {/* Left Column: User Details Card */}
            <Col md={6} className="mb-4">
              <Card className='p-0 rounded-4 shadow-sm h-100'>
                <Card.Body className='p-0'>
                  
                  {/* Header Section (Avatar + Name) */}
                  <div className='row align-items-center p-4 p-md-5 bg-white rounded-top-4'>
                    <div className='col-md-auto text-center mb-3 mb-md-0'>
                      <div
                        className='rounded-circle bg-primary text-white d-flex align-items-center justify-content-center mx-auto position-relative'
                        style={{ width: '140px', height: '140px', cursor: canEditImage ? 'pointer' : 'default' }}
                        onClick={() => canEditImage && setModalShow(true)}
                      >
                        {profile.profile_picture && profile.profile_picture !== 'Default.jpg' ? (
                             <img
                                src={profile.profile_picture}
                                alt='Profile'
                                className='rounded-circle w-100 h-100 object-fit-cover'
                             />
                        ) : (
                             <span style={{ fontSize: '48px', fontWeight: 'bold' }}>
                                {(profile.first_name?.[0] || profile.name?.[0] || '')}
                                {(profile.last_name?.[0] || '')}
                             </span>
                        )}

                        {canEditImage && (
                          <div className='position-absolute bottom-0 end-0 bg-white rounded-circle p-1 shadow-sm' style={{ bottom: '5px', right: '5px' }}>
                            <MdOutlineFileUpload size={24} className="text-primary"/>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <div className='col-md text-center text-md-start'>
                      <h2 className='fw-bold mb-1'>
                        {profile.name || `${profile.first_name || ''} ${profile.last_name || ''}`}
                      </h2>
                      <p className='text-muted mb-0'>
                        Joined on {profile.created_at ? new Date(profile.created_at).toLocaleString('en-US', { month: 'long', year: 'numeric' }) : 'NA'}
                      </p>
                    </div>
                  </div>

                  <div className='bg-light rounded-bottom-4 p-4 p-md-5 position-relative'>
                    
                    <div className='position-absolute top-0 end-0 mt-3 me-3'>
                      {editInfo ? (
                        <FaTimes 
                            size={24} 
                            className="text-secondary cursor-pointer" 
                            onClick={() => setEditInfo(false)} 
                        />
                      ) : (
                        <FaPencilAlt 
                            size={18} 
                            className="text-secondary cursor-pointer" 
                            onClick={() => setEditInfo(true)} 
                        />
                      )}
                    </div>

                    {!editInfo ? (
                      <div className='user-info fs-5'>
                        <Row className='mb-3 align-items-center'>
                          <Col xs="auto" className="text-secondary"><FaPhoneAlt /></Col>
                          <Col className="text-break">+91 {profile.mobile_no || 'NA'}</Col>
                        </Row>
                        <Row className='mb-3 align-items-center'>
                          <Col xs="auto" className="text-secondary"><FaEnvelope /></Col>
                          <Col className="text-break">{profile.email_id || 'NA'}</Col>
                        </Row>
                        <Row className='mb-3 align-items-center'>
                          <Col xs="auto" className="text-secondary"><FaLandmark /></Col>
                          <Col className="text-break">{profile.address || 'NA'}</Col>
                        </Row>
                        <Row className='align-items-center'>
                          <Col xs="auto" className="text-secondary"><FaMapMarkerAlt /></Col>
                          <Col className="text-break">
                            {profile.city}, {profile.state} {profile.pincode && `- ${profile.pincode}`}
                          </Col>
                        </Row>
                      </div>
                    ) : (
                      <div className='user-info-edit'>
                        <UpdateProfile 
                            userData={profile} 
                            setEditInfo={setEditInfo} 
                            role={role}
                        />
                      </div>
                    )}
                  </div>
                </Card.Body>
              </Card>
            </Col>

            {/* Right Column: Stats Card */}
            <Col md={6}>
              <Card className='rounded-4 shadow-sm border-0'>
                <Card.Body className='p-5 text-center'>
                  <h3 className='fw-bold mb-4'>Activity Summary</h3>
                  {role === 'FIELD_OFFICER' && (
                      <>
                        <div className="mb-3">
                            <h1 className="display-4 fw-bold text-primary">{profile.sample_count || 0}</h1>
                            <p className="text-muted fs-5">Samples Collected</p>
                        </div>
                        <div>
                            <h1 className="display-4 fw-bold text-success">{profile.report_count || 0}</h1>
                            <p className="text-muted fs-5">Reports Generated</p>
                        </div>
                      </>
                  )}
                  
                  {role === 'FARMER' && (
                      <div className="text-muted">
                        <p>Welcome to your dashboard. Here you can track all your farm activities and reports.</p>
                      </div>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <ProfilePictureModal
            show={modalShow}
            onHide={() => setModalShow(false)}
          />
        </div>
      )}
    </Wrapper>
  );
}

export default Profile;